import { LocalDate } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { GetReturnTypeFromApiSpec } from "trader.ts/router";
import { Source } from "trader.ts/types/source";
import { dyn, scheduleForCleanup } from "trader.ts/ui";
import { textbox } from "trader.ts/ui.common";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { period } from "../../domain/periods";
import { debounce } from "../../lib/debounce";
import { HHmm, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { renderPeriod, translateWeekday } from "../../translations_specific";
import { renderRoomListHeader, renderRoomListItem } from "../room_list/server";
import {
  renderStudentListHeader,
  renderStudentListItem,
} from "../student_list/client";
import {
  renderTeacherListHeader,
  renderTeacherListItem,
} from "../teacher_list/server";

type result = GetReturnTypeFromApiSpec<typeof routes.doSearchSpec>;

export function render(opts: {
  initialSearch: null | result;
  currentPeriod: period | null;
}): HTMLElement | HTMLElement[] {
  const initialSearch = opts.initialSearch;
  const searchStringS = new Source(initialSearch?.searchString || "");

  const searchStyle =
    "max-width: 480px; margin: 0px auto; display: block; margin-bottom: 16px";

  const id = "classy-search-id";

  const resultS = new Source(opts.initialSearch);

  function runSearch(searchString: string) {
    if (searchString.trim().length >= 2) {
      rpc(routes.doSearchSpec, undefined, { searchString: searchString })
        .then((res) => {
          resultS.set(res);
          history.replaceState(
            {},
            "",
            routes.searchRoute.link({ searchstring: res.searchString }),
          );
        })
        .catch(() => {});
    } else {
      history.replaceState(
        {},
        "",
        routes.searchRoute.link({ searchstring: undefined }),
      );
    }
  }

  const debouncedRunSearch = debounce(runSearch, 200);
  scheduleForCleanup(
    searchStringS.observe((str) => {
      resultS.set(null);
      debouncedRunSearch(str);
    }),
  );

  setTimeout(
    () => (document.querySelector("#" + id) as HTMLInputElement)?.focus(),
    1,
  );

  const today = LocalDate.now();

  return (
    <div>
      <div
        className={style.icon_container + FA.icon_search}
        style={searchStyle}
      >
        {textbox({
          source: searchStringS,
          type: "search",
          placeholder: "Zoek" + "...",
          id,
        })}
      </div>

      {dyn(resultS, (result) => {
        if (result === null) {
          return (
            <div style="width: 100%;text-align: center;font-style: italic;">
              {"Zoek leerlingen op naam of email, docenten, lokalen, facturen en mollie betalingen"}
            </div>
          );
        }
        if (
          result.invoices.length +
            result.rooms.length +
            result.students.length +
            result.teachers.length ===
          0
        ) {
          return (
            <div>
              {"We vonden niet wat je zoekt."}
              <br />
              {"Zoek je een docent of student? Dan kan je zoeken op voornaam, achternaam of emailadres."}
              <br />
              {"Zoek je een factuur? Dan kan je zoeken op het factuurnummer, bvb: 202400015."}
            </div>
          );
        } else {
          const stu =
            result.students.length > 0 ? (
              <table style="margin-bottom: 128px;">
                {renderStudentListHeader()}
                {result.students.map((stu) =>
                  renderStudentListItem(today, stu, result.searchString),
                )}
              </table>
            ) : (
              <span></span>
            );
          const tea =
            result.teachers.length > 0 ? (
              <table style="margin-bottom: 128px;">
                {renderTeacherListHeader()}
                {result.teachers.map(renderTeacherListItem)}
              </table>
            ) : (
              <span></span>
            );

          const roo =
            result.rooms.length > 0 ? (
              <table style="margin-bottom: 128px;">
                {renderRoomListHeader()}
                {result.rooms.map(renderRoomListItem)}
              </table>
            ) : (
              <span></span>
            );
          const inv =
            result.invoices.length > 0 ? (
              <table style="margin-bottom: 128px;">
                {renderInvoiceListHeader()}
                {result.invoices.map(renderInvoiceListItem)}
              </table>
            ) : (
              <span></span>
            );

          const op =
            result.onlinePayments.length > 0 ? (
              <table style="margin-bottom: 128px;">
                {renderOnlinePaymentHeader()}
                {result.onlinePayments.map(renderOnlinePaymentItem)}
              </table>
            ) : (
              <span></span>
            );

          return [tea, stu, roo, inv, op];
        }
      })}
    </div>
  );
}

function renderOnlinePaymentHeader() {
  return (
    <tr>
      <th>{"Online betaling"}</th>
    </tr>
  );
}

function renderOnlinePaymentItem(invoice: result["onlinePayments"][number]) {
  return (
    <tr>
      <td>
        <a
          href={routes.payment_detailRoute.link({
            paymentproviderid: invoice.paymentid,
          })}
        >
          <b>{invoice.paymentid}</b>
        </a>
      </td>
    </tr>
  );
}

function renderInvoiceListHeader() {
  return (
    <tr>
      <th>{"student"}</th>
      <th>{"factuur"}</th>
      <th className={style.remove_on_phone}>{"betaald?"}</th>
      <th className={style.remove_on_phone}>{"hoort bij"}</th>
    </tr>
  );
}

function renderInvoiceListItem(invoice: result["invoices"][number]) {
  return (
    <tr>
      <td>
        <a
          href={routes.studentOverviewRoute.link({
            studentid: invoice.student.id,
          })}
        >
          <b>{renderFirstNameLastName(invoice.student)}</b>
        </a>
      </td>
      <td>
        {invoice.subject.tag === "creditnote" ? (
          <div>#{invoice.subject.cn_externalid.toString()}</div>
        ) : (
          <a
            href={routes.invoicedetailsRoute.link({
              invoiceid: invoice.id,
              edit: false,
            })}
          >
            #{invoice.externalidstring}
          </a>
        )}
      </td>
      <td>{invoice.paid === false ? "Nee" : "Ja"}</td>
      <td className={style.remove_on_phone}>
        {invoice.subject.tag === "enrollment" ? (
          <a
            href={routes.lessongroup_generalRoute.link({
              lessongroupid: invoice.subject.lessongroupid,
            })}
          >
            {"%weekday om %time bij %teacher"
              .replace("%weekday", translateWeekday(invoice.subject.weekday))
              .replace("%time", invoice.subject.time.format(HHmm))
              .replace(
                "%teacher",
                renderFirstNameLastName(invoice.subject.teacher),
              )}
          </a>
        ) : invoice.subject.tag === "creditnote" ? (
          <a
            href={routes.invoicedetailsRoute.link({
              invoiceid: invoice.id,
              edit: false,
            })}
          >
            {"Creditnota van factuur %invoicenr".replace(
              "%invoicenr",
              "#" + invoice.externalidstring,
            )}
          </a>
        ) : (
          <a>
            {"Jaargeld" +
              " " +
              renderPeriod(
                invoice.subject.period.firstday,
                invoice.subject.period.lastday,
              )}
          </a>
        )}
      </td>
    </tr>
  );
}
